import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Modal, Stack, Title } from "@mantine/core";
import { showNotification } from "@mantine/notifications";

import { Deployment } from "@protos/deployment/v1/deployment";
import { createDeployment, refreshUserAuth } from "@shared/api";
import {
  useBasePath,
  useChangeOrg,
  useJoinDeploymentMutation,
  useOrg,
  useUser,
} from "@shared/hooks";
import { ApiError, DeploymentAction, OrgData } from "@shared/types";
import { deploymentProtoToOrg } from "@shared/utils";

import { JoinableOrgsForm } from "./JoinableOrgsForm";
import { NewOrgForm } from "./NewOrgForm";
import { OrgActionSelector } from "./OrgActionSelector";

interface CreateOrgDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  unCloseable?: boolean;
}

export const CreateOrgDialog: React.FC<CreateOrgDialogProps> = ({
  isOpen,
  onClose,
  unCloseable,
}) => {
  const [org] = useOrg();
  const basePath = useBasePath();
  const changeOrg = useChangeOrg();
  const [, setUser] = useUser();
  const history = useHistory();
  const [action, setAction] = useState<DeploymentAction>();

  const { mutate: joinDeployment, isPending } = useJoinDeploymentMutation();

  // If it's a new organization, we will redirect the user directly to the
  // projects page. Otherwise, we will keep them where they are
  const onFinish = async (deployment: OrgData) => {
    const redirectUrl =
      action === "create" ? `${basePath}/projects/scanning` : basePath;
    await refreshUserAuth(setUser);
    changeOrg(deployment);
    history.push(redirectUrl);
  };

  const onCreate = async (values: {
    name: string;
    source_type: string;
    source_id?: string;
  }) => {
    createDeployment(values.name, values.source_type, values.source_id)
      .then((deployment) => onFinish(deployment))
      .catch((err: ApiError) => {
        showNotification({
          message:
            err.message ||
            "There was an issue creating an organization with the provided name.",
        });
      });
  };

  const onJoin = async (deployment: Deployment) => {
    joinDeployment(deployment.id);
    onFinish(deploymentProtoToOrg(deployment));
  };

  return (
    <Modal
      opened={isOpen}
      size="auto"
      closeOnClickOutside={!unCloseable}
      withCloseButton={!unCloseable}
      closeOnEscape={!unCloseable}
      title={org ? "Add an organization" : "Welcome to Semgrep!"}
      onClose={() => {
        setAction(undefined);
        if (onClose) onClose();
      }}
    >
      <Box w="50vw" miw={400}>
        <Stack align="center" p="md" pt={24}>
          {!org && (
            <Title order={3} fw="normal">
              Let's get you started
            </Title>
          )}
          {action === "create" ? (
            <NewOrgForm
              onSubmit={onCreate}
              onBack={() => setAction(undefined)}
              isSaving={isPending}
            />
          ) : action === "join" ? (
            <JoinableOrgsForm
              onJoin={onJoin}
              onBack={() => setAction(undefined)}
              isSaving={isPending}
            />
          ) : (
            <OrgActionSelector onJoin={onJoin} setAction={setAction} />
          )}
        </Stack>
      </Box>
    </Modal>
  );
};
